/**
 * New Relic Browser (RUM) helpers.
 *
 * The Browser agent is loaded via public/index.html and exposed as the global
 * `window.newrelic`. These wrappers are null-safe so the app never breaks when
 * the agent is absent (local dev, blocked script, etc.).
 *
 * Custom attributes set here are attached to every subsequent PageView,
 * PageAction, AjaxRequest and BrowserInteraction event, enabling the New Relic
 * usage dashboard to facet frontend activity by user, role and facility.
 */

const getAgent = () =>
  typeof window !== "undefined" &&
  window.newrelic &&
  typeof window.newrelic.setCustomAttribute === "function"
    ? window.newrelic
    : null

/**
 * Tag every RUM event with the deployment environment (dev/uat/prod). Browser
 * events carry no environment attribute otherwise, so the usage dashboard
 * cannot separate dev/uat/prod frontend traffic. Persisted so it survives SPA
 * navigations; call at app bootstrap so pre-login PageViews are tagged too.
 */
export const setNewRelicEnvironment = (
  environment = process.env.REACT_APP_ENV,
) => {
  const agent = getAgent()
  if (!agent) return
  const env = environment == null ? "" : String(environment).trim()
  if (!env) return
  try {
    agent.setCustomAttribute("environment", env, true)
  } catch {
    // Telemetry must never interrupt app startup.
  }
}

/**
 * Attach the authenticated user's context to all future RUM events.
 * Values are persisted so they survive SPA navigations and reloads.
 */
export const setNewRelicUserContext = ({ userId, role, facilityId }) => {
  const agent = getAgent()
  if (!agent) return
  try {
    if (userId != null) agent.setCustomAttribute("userId", String(userId), true)
    if (role != null) agent.setCustomAttribute("role", String(role), true)
    if (facilityId != null)
      agent.setCustomAttribute("facilityId", String(facilityId), true)
  } catch {
    // Telemetry must never interrupt the login flow.
  }
}

/** Clear user context on logout so events aren't misattributed. */
export const clearNewRelicUserContext = () => {
  const agent = getAgent()
  if (!agent || typeof agent.setCustomAttribute !== "function") return
  try {
    agent.setCustomAttribute("userId", null)
    agent.setCustomAttribute("role", null)
    agent.setCustomAttribute("facilityId", null)
  } catch {
    // no-op
  }
}

/**
 * Record a client-side feature interaction (e.g. SPA route view, feature click)
 * that the Experience API never sees. Surfaces in the PageAction event type.
 */
export const trackFeatureView = (featureArea, attributes = {}) => {
  const agent = getAgent()
  if (!agent || typeof agent.addPageAction !== "function") return
  try {
    agent.addPageAction("feature_view", { featureArea, ...attributes })
  } catch {
    // no-op
  }
}
